import { BPButton } from "@/components/bp/bpbutton";
import React, { useEffect, useRef, useState } from "react";
import { Modal, Image, Button, Icon, ModalContent, ModalActions, ModalHeader } from "@/components/bp/bp-ui";

export interface IUploadedImage {
    id?: number | null;
    fileName: string;
    mimeType?: string;
    size?: number;
    base64DataUrl: string;
    file?: File;
}

export interface ImageModalProps {
    images: IUploadedImage[];
    open: boolean;
    initialIndex?: number | null;
    onClose: () => void;
    onRemove?: (idx: number) => void;

    // Optional features (defaults provided)
    enableKeyboardNavigation?: boolean;
    enableZoomPan?: boolean;
    showMetadata?: boolean;
}

export const ImageModal: React.FC<ImageModalProps> = ({
    images,
    open,
    initialIndex = null,
    onClose,
    onRemove,
    enableKeyboardNavigation = false,
    enableZoomPan = false,
    showMetadata = false
}) => {
    const [index, setIndex] = useState<number | null>(initialIndex);
    const [zoom, setZoom] = useState<number>(1);
    const [panX, setPanX] = useState<number>(0);
    const [panY, setPanY] = useState<number>(0);
    const [isPanning, setIsPanning] = useState<boolean>(false);
    const panStart = useRef<{ x: number; y: number } | null>(null);

    useEffect(() => {
        setIndex(initialIndex);
    }, [initialIndex]);

    useEffect(() => {
        // reset zoom/pan when image changes or modal closes/opens
        setZoom(1);
        setPanX(0);
        setPanY(0);
    }, [index, open]);

    // keyboard navigation
    useEffect(() => {
        if (!open || !enableKeyboardNavigation) return;

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft") {
                e.preventDefault();
                showPrev();
            } else if (e.key === "ArrowRight") {
                e.preventDefault();
                showNext();
            } else if (e.key === "Escape") {
                e.preventDefault();
                onClose();
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open, enableKeyboardNavigation, index, images]);

    const showPrev = () => {
        if (index === null || images.length === 0) return;
        setIndex((index - 1 + images.length) % images.length);
    };

    const showNext = () => {
        if (index === null || images.length === 0) return;
        setIndex((index + 1) % images.length);
    };

    const changeZoom = (delta: number) => {
        setZoom(prev => Math.max(0.1, Math.min(10, +(prev + delta).toFixed(2))));
    };

    const resetZoomPan = () => {
        setZoom(1);
        setPanX(0);
        setPanY(0);
    };

    const onWheel = (e: React.WheelEvent) => {
        if (!enableZoomPan) return;
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        changeZoom(delta);
    };

    const onMouseDown = (e: React.MouseEvent) => {
        if (!enableZoomPan || zoom <= 1) return;
        setIsPanning(true);
        panStart.current = { x: e.clientX - panX, y: e.clientY - panY };
        (e.target as HTMLElement).setAttribute("draggable", "false");
    };

    const onMouseMove = (e: React.MouseEvent) => {
        if (!isPanning || !panStart.current) return;
        setPanX(e.clientX - panStart.current.x);
        setPanY(e.clientY - panStart.current.y);
    };

    const onMouseUp = () => {
        if (!enableZoomPan) return;
        setIsPanning(false);
        panStart.current = null;
    };

    const onMouseLeave = () => {
        if (!enableZoomPan) return;
        setIsPanning(false);
        panStart.current = null;
    };

    const formatBytes = (bytes?: number) => {
        if (!bytes) return "0 B";
        const k = 1024;
        const sizes = ["B", "KB", "MB", "GB", "TB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    const handleRemove = () => {
        if (index === null || !onRemove) return;
        onRemove(index);
    };

    const current = index !== null && images[index] ? images[index] : null;

    return (
        <Modal size="large" open={open} onClose={onClose} closeIcon>
            <ModalHeader>{current?.fileName}</ModalHeader>
            <ModalContent image>
                {current ? (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            maxHeight: "70vh",
                            overflow: "hidden",
                            position: "relative"
                        }}
                        onWheel={onWheel}
                    >
                        <Image
                            wrapped
                            src={current.base64DataUrl}
                            style={{
                                transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
                                transition: isPanning ? "none" : "transform 0.05s",
                                cursor: enableZoomPan && zoom > 1 ? (isPanning ? "grabbing" : "grab") : undefined,
                                maxWidth: "100%",
                                maxHeight: "70vh",
                                userSelect: "none"
                            }}
                            onMouseDown={onMouseDown}
                            onMouseMove={onMouseMove}
                            onMouseUp={onMouseUp}
                            onMouseLeave={onMouseLeave}
                            draggable={false}
                        />
                        {showMetadata && (
                            <div
                                style={{
                                    position: "absolute",
                                    left: 10,
                                    bottom: 10,
                                    background: "rgba(0,0,0,0.6)",
                                    color: "#fff",
                                    padding: "6px 8px",
                                    borderRadius: 4,
                                    fontSize: 12
                                }}
                            >
                                <div>{current.fileName}</div>
                                <div>{current.mimeType || "unknown type"}</div>
                                <div>{formatBytes(current.size)}</div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div>No image</div>
                )}
            </ModalContent>

            <ModalActions>
                {index !== null && enableKeyboardNavigation && (
                    <>
                        <BPButton icon onClick={showPrev} type="button">
                            <Icon name="chevron left" /> Prev
                        </BPButton>
                        <BPButton icon onClick={showNext} type="button">
                            Next <Icon name="chevron right" />
                        </BPButton>
                    </>
                )}

                {enableZoomPan && (
                    <>
                        <BPButton icon onClick={() => changeZoom(-0.2)} type="button" title="Zoom out">
                            <Icon name="minus" />
                        </BPButton>
                        <BPButton icon onClick={() => resetZoomPan()} type="button" title="Reset zoom & pan">
                            <Icon name="refresh" />
                        </BPButton>
                        <BPButton icon onClick={() => changeZoom(0.2)} type="button" title="Zoom in">
                            <Icon name="plus" />
                        </BPButton>
                    </>
                )}

                {index !== null && onRemove && (
                    <BPButton negative onClick={handleRemove}>
                        <Icon name="trash" /> Remove
                    </BPButton>
                )}
                <BPButton onClick={onClose}>
                    <Icon name="close" /> Close
                </BPButton>
            </ModalActions>
        </Modal>
    );
};

export default ImageModal;